import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import './ReusableDropdown.css';

const ReusableDropdown = ({ 
  // Required props
  triggerText, 
  items, 
  
  // Optional callback props
  onItemClick,
  onToggle,
  onOpen,
  onClose,
  
  // Styling props
  className = '',
  triggerClassName = '',
  menuClassName = '',
  itemClassName = '',
  
  // Display props
  showIcons = true,
  iconComponent: IconComponent = null,
  showArrow = true,
  
  // Behavior props
  hoverToOpen = true,
  clickToOpen = true,
  closeOnItemClick = true,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  
  // Positioning props
  position = 'left', // 'left', 'center', 'right'
  align = 'start', // 'start', 'center', 'end'
  
  // Size props
  size = 'medium', // 'small', 'medium', 'large'
  
  // Variant props
  variant = 'default', // 'default', 'minimal', 'outlined', 'filled'
  
  // Accessibility props
  ariaLabel,
  ariaLabelledBy,
  
  // Custom props
  disabled = false,
  loading = false,
  
  // Mobile-specific props
  mobileBehavior = 'overlay', // 'overlay', 'dropdown'
  
  // Custom render props
  renderTrigger,
  renderItem,
  renderMenu
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const timeoutRef = useRef(null);
  const isHoveringRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuItemsRef = useRef([]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle outside click
  useEffect(() => {
    if (!closeOnOutsideClick || !isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnOutsideClick]);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape]);

  const handleOpen = () => {
    if (disabled || loading) return;
    setIsOpen(true);
    if (onOpen) onOpen();
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    
    // Clear any pending timeouts when manually toggling
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    if (onToggle) onToggle(newIsOpen);
    if (newIsOpen && onOpen) onOpen();
    if (!newIsOpen && onClose) onClose();
  };

  const handleMouseEnter = () => {
    if (!hoverToOpen || disabled || loading) return;
    
    // Disable hover on mobile devices
    if (window.innerWidth <= 991) {
      return;
    }
    
    isHoveringRef.current = true;
    
    // Clear any pending close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Debounce the open action to prevent rapid state changes
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      if (isHoveringRef.current && !isOpen) {
        handleOpen();
      }
      debounceTimeoutRef.current = null;
    }, 50); // Small debounce delay
  };

  const handleMouseLeave = () => {
    if (!hoverToOpen) return;
    
    // Disable hover on mobile devices
    if (window.innerWidth <= 991) {
      return;
    }
    
    isHoveringRef.current = false;
    
    // Clear debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    // Clear any existing close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        handleClose();
      }
      timeoutRef.current = null;
    }, 150);
  };

  const handleMenuMouseEnter = () => {
    if (!hoverToOpen) return;
    
    // Disable hover on mobile devices
    if (window.innerWidth <= 991) {
      return;
    }
    
    isHoveringRef.current = true;
    
    // Clear any pending close timeout when entering menu
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMenuMouseLeave = () => {
    if (!hoverToOpen) return;
    
    // Disable hover on mobile devices
    if (window.innerWidth <= 991) {
      return;
    }
    
    isHoveringRef.current = false;
    
    // Clear any existing close timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        handleClose();
      }
      timeoutRef.current = null;
    }, 150);
  };

  const handleItemClick = (item, event) => {
    // Only prevent default for non-Link items (buttons)
    if (event && event.preventDefault && !item.to) {
      event.preventDefault();
    }
    if (onItemClick) {
      onItemClick(item, event);
    }
    if (closeOnItemClick) {
      handleClose();
      setFocusedIndex(-1);
    }
  };

  // Handle focus on trigger - open dropdown automatically
  const handleTriggerFocus = () => {
    if (disabled || loading) return;
    if (!isOpen) {
      handleOpen();
    }
  };

  // Handle keyboard navigation
  const handleTriggerKeyDown = (event) => {
    if (disabled || loading) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        if (!isOpen) {
          handleOpen();
        }
        // Focus first item
        setFocusedIndex(0);
        setTimeout(() => {
          if (menuItemsRef.current[0]) {
            menuItemsRef.current[0].focus();
          }
        }, 0);
        break;
      
      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        if (!isOpen) {
          handleOpen();
        }
        // Focus last item
        setFocusedIndex(items.length - 1);
        setTimeout(() => {
          if (menuItemsRef.current[items.length - 1]) {
            menuItemsRef.current[items.length - 1].focus();
          }
        }, 0);
        break;
      
      case 'Tab':
        // If dropdown is open and Tab is pressed, close it and continue normal tabbing
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          handleClose();
          setFocusedIndex(-1);
          // Find next focusable element after this dropdown (skip dropdown items)
          if (triggerRef.current) {
            const allFocusable = Array.from(
              document.querySelectorAll('.page-nav-right.desktop-nav a:not([tabindex="-1"]), .page-nav-right.desktop-nav button:not([tabindex="-1"]), .page-nav-right.desktop-nav [tabindex]:not([tabindex="-1"])')
            );
            const currentIndex = allFocusable.indexOf(triggerRef.current);
            const nextElement = allFocusable[currentIndex + 1];
            if (nextElement) {
              setTimeout(() => {
                nextElement.focus();
              }, 0);
            }
          }
        }
        // If dropdown is closed, let Tab work normally
        break;
      
      case 'Enter':
      case ' ':
        if (!isOpen) {
          event.preventDefault();
          handleToggle();
        }
        break;
      
      default:
        break;
    }
  };

  // Handle keyboard navigation on menu items
  const handleMenuItemKeyDown = (event, index) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        const nextIndex = (index + 1) % items.length;
        setFocusedIndex(nextIndex);
        if (menuItemsRef.current[nextIndex]) {
          menuItemsRef.current[nextIndex].focus();
        }
        break;
      
      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        const prevIndex = (index - 1 + items.length) % items.length;
        setFocusedIndex(prevIndex);
        if (menuItemsRef.current[prevIndex]) {
          menuItemsRef.current[prevIndex].focus();
        }
        break;
      
      case 'Tab':
        // Close dropdown and continue tabbing to next nav item (skip dropdown items)
        event.preventDefault();
        event.stopPropagation();
        handleClose();
        setFocusedIndex(-1);
        // Find next focusable element after this dropdown (skip dropdown items)
        if (triggerRef.current) {
          const allFocusable = Array.from(
            document.querySelectorAll('.page-nav-right.desktop-nav a:not([tabindex="-1"]), .page-nav-right.desktop-nav button:not([tabindex="-1"]), .page-nav-right.desktop-nav [tabindex]:not([tabindex="-1"])')
          );
          const currentIndex = allFocusable.indexOf(triggerRef.current);
          const nextElement = allFocusable[currentIndex + 1];
          if (nextElement) {
            setTimeout(() => {
              nextElement.focus();
            }, 0);
          }
        }
        break;
      
      case 'Escape':
        event.preventDefault();
        handleClose();
        setFocusedIndex(-1);
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;
      
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        if (menuItemsRef.current[0]) {
          menuItemsRef.current[0].focus();
        }
        break;
      
      case 'End':
        event.preventDefault();
        setFocusedIndex(items.length - 1);
        if (menuItemsRef.current[items.length - 1]) {
          menuItemsRef.current[items.length - 1].focus();
        }
        break;
      
      default:
        break;
    }
  };

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  // Handle Tab key on menu to prevent tabbing into dropdown items
  const handleMenuKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      handleClose();
      setFocusedIndex(-1);
      // Move focus to next nav item after dropdown (skip dropdown items)
      if (triggerRef.current) {
        const allFocusable = Array.from(
          document.querySelectorAll('.page-nav-right.desktop-nav a:not([tabindex="-1"]), .page-nav-right.desktop-nav button:not([tabindex="-1"]), .page-nav-right.desktop-nav [tabindex]:not([tabindex="-1"])')
        );
        const currentIndex = allFocusable.indexOf(triggerRef.current);
        const nextElement = allFocusable[currentIndex + 1];
        if (nextElement) {
          setTimeout(() => {
            nextElement.focus();
          }, 0);
        }
      }
    }
  };

  // Generate CSS classes based on props
  const getDropdownClasses = () => {
    const baseClasses = ['reusable-dropdown'];
    
    if (size !== 'medium') baseClasses.push(`size-${size}`);
    if (variant !== 'default') baseClasses.push(`variant-${variant}`);
    if (position !== 'left') baseClasses.push(`position-${position}`);
    if (align !== 'start') baseClasses.push(`align-${align}`);
    if (disabled) baseClasses.push('disabled');
    if (loading) baseClasses.push('loading');
    if (className) baseClasses.push(className);
    
    return baseClasses.join(' ');
  };

  const getTriggerClasses = () => {
    const baseClasses = ['reusable-dropdown-trigger'];
    
    if (isOpen) baseClasses.push('active');
    if (disabled) baseClasses.push('disabled');
    if (loading) baseClasses.push('loading');
    if (triggerClassName) baseClasses.push(triggerClassName);
    
    return baseClasses.join(' ');
  };

  const getMenuClasses = () => {
    const baseClasses = ['reusable-dropdown-menu'];
    
    if (isOpen) baseClasses.push('show');
    if (position !== 'left') baseClasses.push(`position-${position}`);
    if (align !== 'start') baseClasses.push(`align-${align}`);
    if (menuClassName) baseClasses.push(menuClassName);
    
    return baseClasses.join(' ');
  };

  const getItemClasses = (item) => {
    const baseClasses = ['reusable-dropdown-item'];
    
    if (item.active) baseClasses.push('active');
    if (item.disabled) baseClasses.push('disabled');
    if (itemClassName) baseClasses.push(itemClassName);
    if (item.className) baseClasses.push(item.className);
    
    return baseClasses.join(' ');
  };

  // Custom render functions
  const renderTriggerContent = () => {
    if (renderTrigger) {
      return renderTrigger({ isOpen, disabled, loading });
    }
    
    return (
      <>
        {IconComponent && showIcons && <IconComponent className="dropdown-icon" />}
        {triggerText}
        {showArrow && <FaChevronDown className="dropdown-arrow" />}
        {loading && <span className="dropdown-loading">⟳</span>}
      </>
    );
  };

  const renderItemContent = (item) => {
    if (renderItem) {
      return renderItem({ item, isOpen });
    }
    
    return (
      <>
        {item.icon && showIcons && <item.icon className="dropdown-item-icon" />}
        <span className="dropdown-item-text">{item.text}</span>
        {item.badge && <span className="dropdown-item-badge">{item.badge}</span>}
      </>
    );
  };

  const renderMenuContent = () => {
    if (renderMenu) {
      return renderMenu({ items, isOpen });
    }
    
    // Initialize menuItemsRef array if needed
    if (menuItemsRef.current.length !== items.length) {
      menuItemsRef.current = items.map(() => null);
    }
    
    return items.map((item, index) => (
      <div key={item.id || index}>
        {item.to ? (
          <Link
            ref={(el) => { menuItemsRef.current[index] = el; }}
            to={item.to}
            className={getItemClasses(item)}
            onClick={(event) => handleItemClick(item, event)}
            onKeyDown={(event) => handleMenuItemKeyDown(event, index)}
            tabIndex={-1}
            disabled={item.disabled}
            aria-selected={focusedIndex === index}
          >
            {renderItemContent(item)}
          </Link>
        ) : (
          <button
            ref={(el) => { menuItemsRef.current[index] = el; }}
            className={getItemClasses(item)}
            onClick={(event) => handleItemClick(item, event)}
            onKeyDown={(event) => handleMenuItemKeyDown(event, index)}
            tabIndex={-1}
            disabled={item.disabled || disabled}
            aria-selected={focusedIndex === index}
          >
            {renderItemContent(item)}
          </button>
        )}
      </div>
    ));
  };

  return (
    <div 
      ref={dropdownRef}
      className={getDropdownClasses()}
      onMouseEnter={hoverToOpen ? handleMouseEnter : undefined}
      onMouseLeave={hoverToOpen ? handleMouseLeave : undefined}
    >
      <button 
        ref={triggerRef}
        className={getTriggerClasses()}
        onClick={clickToOpen ? handleToggle : undefined}
        onFocus={handleTriggerFocus}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={ariaLabel || triggerText}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled || loading}
      >
        {renderTriggerContent()}
      </button>
      
      <div 
        className={getMenuClasses()}
        onMouseEnter={hoverToOpen ? handleMenuMouseEnter : undefined}
        onMouseLeave={hoverToOpen ? handleMenuMouseLeave : undefined}
        onKeyDown={handleMenuKeyDown}
        role="menu"
        aria-label={ariaLabel || triggerText}
      >
        {renderMenuContent()}
      </div>
    </div>
  );
};

export default ReusableDropdown;
